import React, { useState, useEffect, useRef } from "react";
import { X, ChevronLeft, ChevronRight, ExternalLink, FileText } from 'lucide-react';
import useChatAPI from '../hooks/useChatAPI';

const PDFViewer = ({ pdfs, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { apiCall } = useChatAPI();
  const lastUrl = useRef(null); // <-- persist across renders

  useEffect(() => {
    const fetchPreview = async () => {
      const currentPdf = pdfs[currentIndex];
      const urlString = typeof currentPdf === 'string' ? currentPdf : currentPdf.url;
      if (lastUrl.current === urlString) {
        // Already fetched for this URL
        return;
      }
      lastUrl.current = urlString;
      setLoading(true);
      setError(null);
      setPreviewData(null);

      try {
        const currentPdf = pdfs[currentIndex];
        const urlString = typeof currentPdf === 'string' ? currentPdf : currentPdf.url;


        // Get the auth token from localStorage
        const token = localStorage.getItem('access_token');

        // Fetch the PDF through the proxy endpoint

         //const API_BASE_URL = "http://localhost:8000/api/v1";
        const API_BASE_URL = "https://wa-04-productdatadev.azurewebsites.net/api/v1";
        const response = await fetch(`${API_BASE_URL}/chat/preview-proxy?url=${encodeURIComponent(urlString)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });


        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType !== 'application/pdf') {
            throw new Error(`Unexpected content type: ${contentType}`);
          }

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          setPreviewData({
            url: blobUrl,
            type: 'blob',
          });
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to load preview: ${response.status} - ${errorText}`);
        }
      } catch (err) {
        console.error("Error fetching preview:", err);
        setError(err.message || 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();

    return () => {
      if (previewData?.type === 'blob' && previewData.url) {
        URL.revokeObjectURL(previewData.url);
      }
    };
  }, [currentIndex, pdfs]); // Only run when currentIndex or pdfs changes

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? pdfs.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === pdfs.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const currentPdf = pdfs[currentIndex];
  const urlTitle = typeof currentPdf === 'string' ? currentPdf : (currentPdf.title || currentPdf.url);
  const originalUrl = typeof currentPdf === 'string' ? currentPdf : currentPdf.url;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-95 z-[9999] flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 p-2"
        aria-label="Close viewer"
      >
        <X size={32} />
      </button>

      {pdfs.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            className="absolute left-4 text-white hover:text-gray-300 z-10 p-2"
            aria-label="Previous document"
          >
            <ChevronLeft size={48} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="absolute right-4 text-white hover:text-gray-300 z-10 p-2"
            aria-label="Next document"
          >
            <ChevronRight size={48} />
          </button>
        </>
      )}

      <div 
        className="w-full max-w-7xl h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* <div className="bg-gray-900 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <FileText size={20} className="flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate" title={urlTitle}>
                {urlTitle.length > 80 ? `${urlTitle.substring(0, 80)}...` : urlTitle}
              </p>
              <p className="text-xs text-n5 mt-1">
                Document {currentIndex + 1} of {pdfs.length}
              </p>
            </div>
          </div>
          <a
            href={originalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={16} />
            Open Original
          </a>
        </div> */}

        <div className="flex-1 bg-gray-100 overflow-hidden relative">
          {loading ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-white">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading document preview...</p>
            </div>
          ) : error ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-white p-8">
              <FileText size={48} className="text-n5 mb-4" />
              <p className="text-gray-700 mb-2">Preview unavailable</p>
              <p className="text-sm text-gray-500 mb-6 text-center max-w-md">
                {error}
              </p>
              <a
                href={originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
              >
                <ExternalLink size={18} />
                Open in New Tab
              </a>
            </div>
          ) : previewData ? (
            <div className="w-full h-full">
              {previewData.type === 'blob' ? (
                // For blob URLs, use object tag which has better PDF support
                <object
                  data={previewData.url}
                  type="application/pdf"
                  className="w-full h-full"
                  aria-label={`PDF Preview ${currentIndex + 1}`}
                >
                  <embed
                    src={previewData.url}
                    type="application/pdf"
                    className="w-full h-full"
                  />
                  <p className="p-4 text-center">
                    Your browser does not support PDF preview. 
                    <a href={previewData.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-2">
                      Open PDF
                    </a>
                  </p>
                </object>
              ) : previewData.type === 'fallback' ? (
                // Fallback - show message with direct link
                <div className="w-full h-full flex flex-col items-center justify-center bg-white p-8">
                  <FileText size={48} className="text-n5 mb-4" />
                  <p className="text-gray-700 mb-2">Preview requires opening in new tab</p>
                  <p className="text-sm text-gray-500 mb-6 text-center max-w-md">
                    Due to security settings, this document cannot be previewed inline.
                  </p>
                  <a
                    href={previewData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                  >
                    <ExternalLink size={18} />
                    Open Document
                  </a>
                </div>
              ) : (
                // For streaming URLs, use iframe
                <iframe
                  src={previewData.url}
                  className="w-full h-full border-0 bg-white"
                  title={`Document preview ${currentIndex + 1}`}
                  onError={(e) => {
                    console.error('Preview failed, trying fallback');
                    setError('Unable to display preview. The document may require downloading.');
                  }}
                />
              )}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-white">
              <p className="text-gray-500">No preview available</p>
            </div>
          )}
        </div>

        {currentPdf.document_type && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
            <span className="inline-block text-xs bg-gray-800 text-white px-3 py-1 rounded">
              {currentPdf.document_type}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFViewer;